import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';

function Stat({ k, v }) {
  return (
    <div className="max-w-[250px] mx-auto text-center">
      <div className="font-display text-4xl sm:text-5xl text-primary">{v}</div>
      <div className="mt-1 text-sm text-muted-foreground">{k}</div>
    </div>
  );
}

function Home() {
  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-secondary via-background to-background">
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 pt-8 pb-12 sm:pt-12 sm:pb-16">
          <div className="max-w-3xl">
            <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] text-foreground">
              Find the right donor for your family.
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-muted-foreground">
              The open sperm bank is a platform to help intended parents find the best open and known sperm donor for their family planning goals.             
            </p>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-muted-foreground">
              Browse verified donor profiles with detailed medical histories, personality insights, and request video interviews. 
            </p>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-muted-foreground">
              All profiles are verified and all listed vials are FDA cleared and ready for use. 
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/donors"
                className="rounded-full bg-gradient-to-t from-[#3b82f6] via-[#3b82f6] to-[#60a5fa] px-6 py-3 text-white hover:opacity-90 transition"
              >
                View donor profiles
              </Link>
              <a
                href="#how"
                className="rounded-full border border-border bg-background px-6 py-3 hover:bg-secondary transition"
              >
                How it works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-secondary/40 border-y border-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-wider text-primary">Why families choose us</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl">A more personal approach to donor selection.</h2>
            <p className="mt-4 text-muted-foreground">Unlike traditional sperm banks that keep donors anonymous, we believe in transparency and connection. Whether you're using anonymous donors or connecting with known and directed donors for IVF and clinic procedures, our physician-supervised process ensures safety while giving you the information and options you need.</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              { t: "FDA-registered facility", d: "All donations are processed through our physician-supervised, FDA-registered facility meeting the highest safety standards." },
              { t: "Transparent information", d: "Access detailed donor profiles including medical history, genetic screening, education, personality, and personal essays." },
              { t: "Personal connections", d: "Optional video chat interviews let you get to know your donor personally, a unique feature in the industry." },
              { t: "Affordable options", d: "Many donors offer subsidized pricing to help make family building accessible to more intended parents." },
              { t: "Known donor support", d: "If you have a known donor, we provide the legal framework and medical oversight to protect everyone involved." },
              { t: "Dedicated support", d: "Our care coordinators guide you through every step, from selection to shipping and beyond." },
            ].map((s) => (
              <div key={s.t} className="rounded-2xl border border-border bg-card p-6">
                <h3 className="text-xl font-medium">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      

      {/* For Intended Parents */}
      <section id="how" className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-wider text-primary">Your journey to parenthood</div>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl">Know your donor. Trust your choice.</h2>
          <p className="mt-4 text-muted-foreground">Access comprehensive donor profiles with medical histories, personality insights, and optional video interviews. Whether you're looking for anonymous donors or connecting with known and directed donors for IVF and clinic procedures, we provide the legal framework and medical oversight to protect everyone involved. Apply for subsidies to reduce costs and connect personally with donors who want to help you build your family.</p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {[
            { n: "01", t: "Known & directed donors", d: "Connect with known donors through our secure platform. We provide the legal framework and medical oversight for known donor arrangements used in IVF and clinic fertility procedures." },
            { n: "02", t: "Apply for a subsidy", d: "Click 'Apply for Subsidy' on any profile. Donors can choose to release vials at $1,000–$1,200 instead of full retail." },
            { n: "03", t: "Cycle-aligned shipping", d: "Customized shipping timelines aligned with your ovulation window. No corporate red tape." },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl border border-border bg-card p-6">
              <div className="font-display text-3xl text-primary/70">{s.n}</div>
              <h3 className="mt-4 text-xl font-medium">{s.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>


      {/* Stats */}
      <section className="border-y border-border bg-secondary/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 grid grid-cols-2 md:grid-cols-3 gap-8">
          <Stat v="Up to 100%" k="subsidies available" />
          <Stat v="100%" k="physician-screened" />
          <Stat v="Compliant" k="All vials meet FDA guidelines and are ready for clinical use" />
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
        <div className="rounded-3xl bg-gradient-to-tl from-[#1e3a8a] to-[#3b82f6] text-primary-foreground p-10 sm:p-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-3xl sm:text-5xl max-w-xl">Start your family journey today.</h2>
            <p className="mt-3 max-w-md opacity-90">Browse donor profiles, connect with known and directed donors for IVF and clinic procedures, connect through video chats, and explore subsidized options. Our team is here to support you every step of the way.</p>
          </div>
          <Link to="/donors" className="rounded-full bg-gradient-to-t from-[#3b82f6] via-[#3b82f6] to-[#60a5fa] text-white px-6 py-3 hover:opacity-90 transition whitespace-nowrap">
            View donor profiles →
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 text-sm text-muted-foreground flex flex-col sm:flex-row gap-2 justify-between">
          <div>© {new Date().getFullYear()} The Open Sperm Bank, LLC</div>
          <div>FDA registered · Physician-supervised · HIPAA</div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
